import { ArrowUpRight, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import { useIsDark } from "../../../client/components/ui/card-section-header";
import useTranslation from "../../../client/hooks/useTranslation";
import { SessionChart } from "../../../trading-sessions/SessionChart";
import { SessionErrorBadges } from "../../../trading-sessions/SessionErrorBadges";
import { sessionChartWindow } from "../../../trading-sessions/sessionUtils";
import { useLiteData } from "../../data/LiteDataProvider";
import { Card, Pnl } from "../../ui/ui";
import { Chip, MoodChip, PerfChip, useDurationLabel } from "./SessionBits";
import { durationMinutes, type SessionRow } from "./sessionData";

/** Classes qui fondent la carte du graphique Advanced dans une carte Lite. */
export const embedChartCls = "[&>div]:border-0 [&>div]:shadow-none [&>div:hover]:shadow-none [&>div]:bg-transparent [&>div]:rounded-none";

/** Widget « Dernière session » de l'Advanced : résumé, graphique des trades, lien vers le détail. */
export function LastSession({ row }: { row: SessionRow }) {
  const t = useTranslation();
  const { fmt, rawAccounts } = useLiteData();
  const isDark = useIsDark();
  const duration = useDurationLabel();
  const s = row.session;
  const raw = row.trades.map(tr => tr.raw);

  return (
    <Card
      title={t.dashboard.widgets.lastSession}
      action={
        <Link to={`/session/${s.id}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline shrink-0">
          {t.session.lastSessionOpen}<ArrowUpRight size={13} />
        </Link>
      }
      pad={false}
    >
      <div className="px-5 pt-3 pb-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium truncate">{s.name || fmt.dateLong(s.startedAt!)}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {s.name ? `${fmt.dateLong(s.startedAt!)} · ` : ""}{fmt.time(new Date(s.startedAt!).toISOString())} · {duration(durationMinutes(s))}
            </div>
          </div>
          <Pnl value={row.pnl} decimals className="text-xl font-semibold shrink-0" />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <MoodChip mood={s.mood} />
          <PerfChip performance={s.performance} />
          <Chip>{t.lite.common.trades(row.trades.length)}</Chip>
          {s.videoUrl && <Chip><Youtube size={12} />{t.session.lastSessionVideo}</Chip>}
        </div>
        {(s.maxRunup != null || s.maxDrawdown != null) && (
          <div className="flex gap-5 text-xs">
            {s.maxRunup != null && <span className="text-muted-foreground">{t.session.lastSessionRunup} <span className="font-mono text-pnlPositive">{fmt.money(s.maxRunup, { sign: false })}</span></span>}
            {s.maxDrawdown != null && <span className="text-muted-foreground">{t.session.lastSessionDrawdown} <span className="font-mono text-pnlNegative">{fmt.money(Math.abs(s.maxDrawdown), { sign: false })}</span></span>}
          </div>
        )}
        <SessionErrorBadges errors={s.errors} />
      </div>
      {/* Le graphique ne s'affiche que si la session a des trades (comportement de SessionChart). */}
      {raw.length > 0 && (
        <div className={`h-[400px] border-t border-border ${embedChartCls}`}>
          <SessionChart session={sessionChartWindow(s, raw)} trades={raw} accounts={rawAccounts} isDark={isDark} fill />
        </div>
      )}
    </Card>
  );
}
